import React from 'react';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import AccessibilityOutlinedIcon from '@mui/icons-material/AccessibilityOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { MODULE_KEYS } from '../../pages/appointment/modules/appointmentModules';
import { getScale } from '../../pages/appointment/modules/scales';
import { exampleValue } from '../../pages/appointment/modules/scales/scaleValues';
import ScaleInput from '../../pages/appointment/components/scales/ScaleInput';
import styles from './ModulePreview.module.css';

/**
 * Maquetes estáticas de cada módulo, mostradas na configuração dos tipos de
 * atendimento para o usuário conhecer o card antes de adicioná-lo. São
 * propositalmente esquemáticas (sem dados, sem API, sem interação): dão a
 * ideia do card real sem depender dele.
 */

const Card = ({ title, icon, description, action, children }) => (
    <div className={styles.card}>
        <div className={styles.header}>
            <AreaTitle Title={title} Icon={icon} Description={description} size="small" />
            {action && <span className={styles.button}>{action}</span>}
        </div>
        <div className={styles.divider} />
        {children}
    </div>
);

const Field = ({ label, placeholder, lines = 0 }) => (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        {placeholder && <span className={styles.fieldPlaceholder}>{placeholder}</span>}
        {lines > 0 && (
            <div className={styles.lines}>
                {Array.from({ length: lines }).map((_, index) => (
                    <div key={index} className={styles.line} style={{ width: `${90 - index * 18}%` }} />
                ))}
            </div>
        )}
    </div>
);

const NotesPreview = () => (
    <Card title="Anotações" icon={<StickyNote2OutlinedIcon />} description="Anotações livres deste atendimento" action="Salvar anotação">
        <Field label="Anotações" placeholder="Texto livre com negrito, itálico, sublinhado e listas..." lines={3} />
        <div className={styles.item}>
            <span className={styles.chip}>12/03/2026</span>
            <span className={styles.itemText}>
                Paciente relatou melhora do sono após a última sessão. Orientado a manter os exercícios em casa.
            </span>
        </div>
    </Card>
);

const ComplaintsPreview = () => (
    <Card title="Queixas Principais" icon={<MedicalInformationOutlinedIcon />} description="Lista vigente do paciente" action="Adicionar">
        <Field label="Nova queixa" placeholder="Digite e pressione Enter" />
        <div className={styles.chips}>
            {['Dor lombar', 'Cefaleia tensional', 'Insônia'].map((label) => (
                <span key={label} className={`${styles.chip} ${styles.chipAccent}`}>
                    {label}
                    <CloseIcon sx={{ fontSize: 12 }} />
                </span>
            ))}
        </div>
    </Card>
);

const EvolutionPreview = () => (
    <>
        <Card title="Registro de Atendimento" icon={<AssignmentOutlinedIcon />} description="Preenchido durante a consulta" action="Salvar rascunho">
            <div className={styles.tabs}>
                <span className={`${styles.tab} ${styles.tabActive}`}>Queixa</span>
                <span className={styles.tab}>Evolução</span>
            </div>
            <Field label="Queixa" placeholder="Descreva detalhadamente a queixa do paciente..." lines={2} />
            <Field label="Evolução" placeholder="Descreva os procedimentos realizados e a evolução do paciente..." lines={2} />
        </Card>
        <Card title="Evolução do Paciente" icon={<AssignmentOutlinedIcon />} description="Histórico das consultas anteriores">
            <div className={styles.item}>
                <div className={styles.itemRow}>
                    <span className={styles.chip}>12/03/2026</span>
                    <span className={styles.itemMeta}>Dra. Ana</span>
                </div>
                <span className={styles.itemTitle}>Queixa</span>
                <span className={styles.itemText}>Dor lombar há duas semanas, piora ao sentar.</span>
                <span className={styles.itemTitle}>Evolução</span>
                <span className={styles.itemText}>Mobilização lombar e orientação postural. Boa resposta.</span>
            </div>
        </Card>
    </>
);

const AnamnesisPreview = () => (
    <Card title="Anamnese" icon={<HistoryEduOutlinedIcon />} description="Formulários do paciente" action="Preencher anamnese">
        <div className={styles.item}>
            <div className={styles.itemRow}>
                <span className={styles.itemTitle}>Anamnese inicial</span>
                <span className={`${styles.chip} ${styles.chipAccent}`}>Deste atendimento</span>
            </div>
            <span className={styles.itemText}><strong>Pratica atividade física?</strong> Sim, 2x por semana</span>
            <span className={styles.itemText}><strong>Usa medicação contínua?</strong> Não</span>
            <span className={styles.itemText}><strong>Já fez cirurgia?</strong> Apendicectomia em 2019</span>
        </div>
    </Card>
);

const CRANIAL_ROWS = [
    ['12/03/2026', '14,2', '11,8', 'Leve', '14,5', '13,4', 'Moderada'],
    ['26/03/2026', '14,0', '11,9', 'Normal', '14,3', '13,6', 'Leve'],
];

const CranialPreview = () => (
    <Card title="Assimetria craniana" icon={<MonitorHeartOutlinedIcon />} description="Medições com evolução e classificação" action="Adicionar">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Data', 'AP', 'BP', 'Escala AP/BP', 'DD', 'DE', 'Plagiocefalia'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {CRANIAL_ROWS.map((row) => (
                    <tr key={row[0]}>
                        {row.map((cell, index) => <td key={index}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    </Card>
);

const GROWTH_ROWS = [
    ['10/02/2026', '2 meses', '5,6', '58,4', '39,1', '16,4'],
    ['12/04/2026', '4 meses', '7,0', '63,9', '41,6', '17,1'],
];

const GrowthPreview = () => (
    <Card title="Curva de crescimento" icon={<ShowChartOutlinedIcon />} description="Peso, comprimento/altura e PC nas curvas da OMS" action="Nova medição">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Data', 'Idade', 'Peso (kg)', 'Comp. (cm)', 'PC (cm)', 'IMC'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {GROWTH_ROWS.map((row) => (
                    <tr key={row[0]}>
                        {row.map((cell, index) => <td key={index}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    </Card>
);

const MILESTONE_ROWS = [
    ['Senta sem apoio', 'Dentro da janela', '12/03/2026', '6 meses'],
    ['Fica em pé com apoio', 'Dentro da janela', '20/04/2026', '7 meses'],
    ['Engatinha', 'Na janela de conquista', '—', '—'],
];

const MotorMilestonesPreview = () => (
    <Card title="Marcos motores" icon={<ChildCareOutlinedIcon />} description="Conquistas motoras do bebê nas janelas da OMS">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Marco', 'Situação', 'Conquistado em', 'Idade'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {MILESTONE_ROWS.map((row) => (
                    <tr key={row[0]}>
                        {row.map((cell, index) => <td key={index}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    </Card>
);

const AIMS_ROWS = [
    ['12/05/2026', '6 meses', '9', '6', '4', '3', '22/58', 'P50'],
    ['10/03/2026', '4 meses', '6', '5', '2', '2', '15/58', 'P40'],
];

const AimsPreview = () => (
    <Card title="Escala motora de Alberta (AIMS)" icon={<AccessibilityNewOutlinedIcon />} description="Escore por postura, total e percentil, de 0 a 18 meses" action="Nova avaliação">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Data', 'Idade', 'Prono', 'Supino', 'Sentado', 'Em pé', 'Total', 'Percentil'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {AIMS_ROWS.map((row) => (
                    <tr key={row[0]}>
                        {row.map((cell, index) => <td key={index}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
        <span className={styles.itemMeta}>Escores da aplicação com o material oficial; a faixa sai do percentil.</span>
    </Card>
);

const THERMOGRAPHY_ROWS = [
    ['Trapézio superior', '33,1', '33,3', '−0,2'],
    ['Joelho anterior', '31,4', '30,2', '+1,2'],
    ['Esterno', '34,0', '', '—'],
];

const ThermographyPreview = () => (
    <Card title="Termografia" icon={<DeviceThermostatOutlinedIcon />} description="Temperatura da pele por local, com diferença entre os lados" action="Nova avaliação">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Local', 'Direito (°C)', 'Esquerdo (°C)', 'ΔT (D − E)'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {THERMOGRAPHY_ROWS.map((row) => (
                    <tr key={row[0]}>
                        <td>{row[0]}</td>
                        {row[2] ? <><td>{row[1]}</td><td>{row[2]}</td></> : <td colSpan={2}>{row[1]}</td>}
                        <td>{row[3]}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <span className={styles.itemMeta}>Diferença acima de 0,7 °C entre os lados aparece destacada.</span>
    </Card>
);

const BODY_ROWS = [
    ['12/03/2026', '82,4', '176,0', '26,6', '88,0', '96,5'],
    ['09/04/2026', '80,9', '176,0', '26,1', '86,5', '95,0'],
];

const BodyMeasurementsPreview = () => (
    <Card title="Medidas corporais" icon={<StraightenOutlinedIcon />} description="Peso, altura e circunferências com evolução" action="Nova avaliação">
        <table className={styles.table}>
            <thead>
                <tr>
                    {['Data', 'Peso (kg)', 'Altura (cm)', 'IMC', 'Cintura (cm)', 'Quadril (cm)'].map((header) => (
                        <th key={header}>{header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {BODY_ROWS.map((row) => (
                    <tr key={row[0]}>
                        {row.map((cell, index) => <td key={index}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
        <span className={styles.itemMeta}>Visão geral na silhueta do paciente, com a variação em relação à avaliação anterior.</span>
    </Card>
);

const EVOLUTION_PHOTO_ITEMS = [
    ['12/01/2026', 'Abdômen · Frente', 'Antes'],
    ['12/03/2026', 'Abdômen · Frente', 'Depois'],
    ['12/03/2026', 'Abdômen · Lado direito', ''],
];

const EvolutionPhotosPreview = () => (
    <Card title="Fotos de evolução" icon={<PhotoLibraryOutlinedIcon />} description="Fotos por data, região e ângulo, com comparação" action="Adicionar fotos">
        <div className={styles.thumbs}>
            {EVOLUTION_PHOTO_ITEMS.map(([date, label, badge]) => (
                <div key={`${date}-${label}`} className={styles.thumb}>
                    <div className={styles.thumbImage}><ImageOutlinedIcon /></div>
                    <span className={styles.thumbCaption}>{badge ? `${badge} · ${date}` : date}</span>
                    <span className={styles.thumbCaption}>{label}</span>
                </div>
            ))}
        </div>
        <span className={styles.itemMeta}>Duas fotos selecionadas abrem a comparação lado a lado ou com controle deslizante.</span>
    </Card>
);

const BODY_MAP_ITEMS = [
    ['1', 'Ombro direito', 'Queixa · 7/10', 'Dor ao elevar o braço, piora à noite.'],
    ['2', 'Lombar', 'Observação', 'Tensão à palpação.'],
    ['3', 'Joelho esquerdo', 'Área avaliada', ''],
];

const BodyMapPreview = () => (
    <Card title="Mapa corporal" icon={<AccessibilityOutlinedIcon />} description="Pontos de queixa e observação na silhueta" action="Frente · Costas">
        {BODY_MAP_ITEMS.map(([number, region, summary, note]) => (
            <div key={number} className={styles.item}>
                <div className={styles.itemRow}>
                    <span className={styles.itemTitle}>
                        <span className={styles.chipAccent} style={{ marginRight: '8px' }}>{number}</span>
                        {region}
                    </span>
                    <span className={styles.itemMeta}>{summary}</span>
                </div>
                {note && <span className={styles.itemText}>{note}</span>}
            </div>
        ))}
        <span className={styles.itemMeta}>Cada clique na silhueta cria um marcador numerado; os mapas anteriores podem ser sobrepostos.</span>
    </Card>
);

const APPLICATION_MAP_ITEMS = [
    ['1', 'Toxina botulínica', '#7C5CFF', 'Glabela · 4 UI', 'Ponto'],
    ['2', 'Toxina botulínica', '#7C5CFF', 'Frontal esquerdo · 2 UI', 'Ponto'],
    ['3', 'Ácido hialurônico', '#FF6B9A', 'Lábio superior · 0,5 ml', 'Traço com direção'],
];

const ApplicationMapPreview = () => (
    <Card title="Mapa de aplicação" icon={<VaccinesOutlinedIcon />} description="Pontos e traços com produto e quantidade" action="Rosto · Frente · Costas">
        {APPLICATION_MAP_ITEMS.map(([number, product, color, summary, kind]) => (
            <div key={number} className={styles.item}>
                <div className={styles.itemRow}>
                    <span className={styles.itemTitle}>
                        <span className={styles.chipAccent} style={{ marginRight: '8px', background: color, color: '#fff' }}>{number}</span>
                        {product}
                    </span>
                    <span className={styles.itemMeta}>{kind}</span>
                </div>
                <span className={styles.itemText}>{summary}</span>
            </div>
        ))}
        <span className={styles.itemMeta}>Totais da sessão: Toxina botulínica 6 UI · Ácido hialurônico 0,5 ml. Os produtos e as cores são da clínica.</span>
    </Card>
);

const MEAL_PLAN_ITEMS = [
    ['Café da manhã', '07:30', [['Pão integral', '2 fatias', 'ou Tapioca, 1 unidade'], ['Ovo mexido', '2 unidades', null]]],
    ['Almoço', '12:30', [['Arroz integral', '4 colheres de sopa', 'ou Batata-doce, 150 g'], ['Frango grelhado', '120 g', null], ['Salada de folhas', '1 prato', null]]],
];

const MealPlanPreview = () => (
    <Card title="Plano alimentar" icon={<RestaurantMenuOutlinedIcon />} description="Refeições, porções e substituições" action="Nova versão · PDF">
        {MEAL_PLAN_ITEMS.map(([meal, time, items]) => (
            <div key={meal} className={styles.item}>
                <div className={styles.itemRow}>
                    <span className={styles.itemTitle}>{meal}</span>
                    <span className={styles.itemMeta}>{time}</span>
                </div>
                {items.map(([food, portion, substitute]) => (
                    <div key={food} className={styles.itemRow}>
                        <span className={styles.itemText}>{food}{substitute && <span className={styles.itemMeta} style={{ marginLeft: '8px' }}>{substitute}</span>}</span>
                        <span className={styles.itemMeta}>{portion}</span>
                    </div>
                ))}
            </div>
        ))}
        <span className={styles.itemMeta}>O paciente recebe o plano em PDF; cada nova versão guarda a anterior no histórico.</span>
    </Card>
);

const DiagnosesPreview = () => (
    <Card title="Diagnósticos (CID)" icon={<LocalHospitalOutlinedIcon />} description="Diagnósticos da consulta pela CID-10 ou pela CID-11">
        <Field label="Buscar na CID-10" placeholder="Código ou nome, por exemplo F32.1 ou enxaqueca" />
        <div className={styles.chips}>
            {[['G43.9', 'Enxaqueca, sem especificação'], ['G44.2', 'Cefaléia tensional']].map(([code, title]) => (
                <span key={code} className={`${styles.chip} ${styles.chipAccent}`}>
                    <b>{code}</b> {title}
                    <CloseIcon sx={{ fontSize: 12 }} />
                </span>
            ))}
        </div>
        <span className={styles.itemMeta}>CID-10 ou CID-11, com códigos e títulos oficiais; os diagnósticos das consultas anteriores ficam listados por data.</span>
    </Card>
);

const DocumentsPreview = () => (
    <Card title="Documentos emitidos" icon={<DescriptionOutlinedIcon />} description="Atestados, receitas, recibos e outros" action="Emitir">
        {[['Atestado médico', '12/03/2026'], ['Receita simples', '12/03/2026'], ['Recibo', '12/03/2026']].map(([label, date]) => (
            <div key={label} className={styles.item}>
                <div className={styles.itemRow}>
                    <span className={styles.itemTitle}>
                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: '6px' }} />
                        {label}
                    </span>
                    <span className={styles.itemMeta}>
                        {date}
                        <DownloadOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', marginLeft: '8px' }} />
                    </span>
                </div>
            </div>
        ))}
    </Card>
);

const AttachmentsPreview = () => (
    <Card title="Arquivos" icon={<FolderOutlinedIcon />} description="Fotos e arquivos do paciente" action="Anexar">
        <div className={styles.thumbs}>
            {['Foto frontal', 'Exame de sangue', 'Raio-X'].map((label) => (
                <div key={label} className={styles.thumb}>
                    <div className={styles.thumbImage}><ImageOutlinedIcon /></div>
                    <span className={styles.thumbCaption}>{label}</span>
                </div>
            ))}
        </div>
    </Card>
);

const PREVIEWS = {
    [MODULE_KEYS.NOTES]: NotesPreview,
    [MODULE_KEYS.COMPLAINTS]: ComplaintsPreview,
    [MODULE_KEYS.EVOLUTION]: EvolutionPreview,
    [MODULE_KEYS.ANAMNESIS]: AnamnesisPreview,
    [MODULE_KEYS.CRANIAL_ASYMMETRY]: CranialPreview,
    [MODULE_KEYS.GROWTH_CURVE]: GrowthPreview,
    [MODULE_KEYS.MOTOR_MILESTONES]: MotorMilestonesPreview,
    [MODULE_KEYS.AIMS]: AimsPreview,
    [MODULE_KEYS.BODY_MEASUREMENTS]: BodyMeasurementsPreview,
    [MODULE_KEYS.EVOLUTION_PHOTOS]: EvolutionPhotosPreview,
    [MODULE_KEYS.BODY_MAP]: BodyMapPreview,
    [MODULE_KEYS.MEAL_PLAN]: MealPlanPreview,
    [MODULE_KEYS.APPLICATION_MAP]: ApplicationMapPreview,
    [MODULE_KEYS.THERMOGRAPHY]: ThermographyPreview,
    [MODULE_KEYS.DIAGNOSES]: DiagnosesPreview,
    [MODULE_KEYS.DOCUMENTS]: DocumentsPreview,
    [MODULE_KEYS.ATTACHMENTS]: AttachmentsPreview,
};

// Escalas clínicas: a própria entrada do card, só leitura, com um valor de exemplo
const ScalePreview = ({ scale }) => {
    const Icon = scale.icon || TuneOutlinedIcon;
    return (
        <Card title={scale.name} icon={<Icon />} description={scale.description}>
            <ScaleInput scale={scale} value={exampleValue(scale)} onChange={() => {}} readOnly />
            <span className={styles.itemMeta}>Um clique marca o resultado; o card mostra o da última consulta.</span>
        </Card>
    );
};

const ModulePreview = ({ moduleKey }) => {
    const Preview = PREVIEWS[moduleKey];
    if (Preview) return <Preview />;
    const scale = getScale(moduleKey);
    return scale ? <ScalePreview scale={scale} /> : null;
};

export default ModulePreview;
